import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Factory,
  HardHat,
  Hotel,
  Store,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { ROUTES } from '@shared/constants/routes';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

interface CaseStudy {
  slug: string;
  sector: string;
  sectorIcon: LucideIcon;
  pole: 'Travaux' | 'Courtage énergie' | 'Mandataire CEE';
  title: string;
  client: string;
  region: string;
  metrics: { value: string; label: string }[];
  tone: 'brand' | 'accent' | 'lavender' | 'sage';
}

const CASES: CaseStudy[] = [
  {
    slug: 'logistique-sud-ouest',
    sector: 'Logistique',
    sectorIcon: Factory,
    pole: 'Mandataire CEE',
    title: 'Plateforme logistique 28 000 m² — éclairage LED et pilotage bâtiment.',
    client: 'Groupe logistique régional',
    region: 'Nouvelle-Aquitaine',
    metrics: [
      { value: '187 k€', label: 'prime CEE estimée' },
      { value: '−32 %', label: 'consommation site' },
      { value: 'J+38', label: 'délai versement' },
    ],
    tone: 'accent',
  },
  {
    slug: 'pme-tpe-bretagne',
    sector: 'Services',
    sectorIcon: Building2,
    pole: 'Courtage énergie',
    title: 'TPE 18 salariés — renégociation contrat tri-tarif élec.',
    client: 'Cabinet de conseil',
    region: 'Bretagne',
    metrics: [
      { value: '11,2 k€', label: 'économies / an' },
      { value: '−14,8 %', label: 'tarif négocié' },
      { value: '0', label: 'coupure de service' },
    ],
    tone: 'lavender',
  },
  {
    slug: 'hotel-paris',
    sector: 'Hôtellerie',
    sectorIcon: Hotel,
    pole: 'Travaux',
    title: 'Hôtel 4★ 84 chambres — pompe à chaleur + isolation + pilotage bâtiment.',
    client: 'Hôtel indépendant',
    region: 'Île-de-France',
    metrics: [
      { value: '316 k€', label: 'aides cumulées' },
      { value: '−41 %', label: 'facture gaz' },
      { value: '7,8 ans', label: 'retour sur investissement' },
    ],
    tone: 'brand',
  },
  {
    slug: 'artisan-rge-44',
    sector: 'Artisan RGE',
    sectorIcon: HardHat,
    pole: 'Mandataire CEE',
    title: 'Artisan PAC — 47 chantiers par an, primes gérées intégralement.',
    client: 'Plomberie / chauffage RGE',
    region: 'Loire-Atlantique',
    metrics: [
      { value: '0 €', label: 'avance trésorerie' },
      { value: '47', label: 'chantiers / an' },
      { value: 'J+30', label: 'versement moyen' },
    ],
    tone: 'accent',
  },
  {
    slug: 'retail-grand-est',
    sector: 'Retail',
    sectorIcon: Store,
    pole: 'Courtage énergie',
    title: 'Enseigne 22 magasins — audit factures + courtage gaz/élec.',
    client: 'Réseau retail régional',
    region: 'Grand Est',
    metrics: [
      { value: '142 k€', label: 'éco. annuelles' },
      { value: '22', label: 'sites optimisés' },
      { value: '−8,4 %', label: 'tarif moyen' },
    ],
    tone: 'lavender',
  },
];

const TONE = {
  brand: { badge: 'bg-brand-50 text-brand-700 ring-brand-200/60', border: 'border-brand-100' },
  accent: { badge: 'bg-accent-50 text-accent-800 ring-accent-200/60', border: 'border-accent-100' },
  lavender: { badge: 'bg-lavender-50 text-lavender-700 ring-lavender-200/60', border: 'border-lavender-100' },
  sage: { badge: 'bg-sage-100 text-sage-500 ring-sage-200/60', border: 'border-sage-200' },
} as const;

export function CaseClientsCarousel() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateBounds = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    const cardW = el.firstElementChild?.firstElementChild?.clientWidth ?? 1;
    setActiveIdx(Math.round(el.scrollLeft / cardW));
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateBounds();
    el.addEventListener('scroll', updateBounds, { passive: true });
    window.addEventListener('resize', updateBounds);
    return () => {
      el.removeEventListener('scroll', updateBounds);
      window.removeEventListener('resize', updateBounds);
    };
  }, []);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardW = el.firstElementChild?.firstElementChild?.clientWidth ?? el.clientWidth * 0.85;
    el.scrollBy({ left: dir * (cardW + 12), behavior: 'smooth' });
  };

  const scrollToIdx = (idx: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardW = el.firstElementChild?.firstElementChild?.clientWidth ?? el.clientWidth * 0.85;
    el.scrollTo({ left: idx * (cardW + 12), behavior: 'smooth' });
  };

  return (
    <section
      aria-labelledby="cases-carousel-title"
      className="relative overflow-hidden bg-stone-50/40 py-16 md:py-24"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 top-12 h-[420px] w-[420px] rounded-full bg-brand-200/15 blur-[140px]"
      />

      <Container>
        <Reveal>
          <div className="mb-10 flex flex-col gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="eyebrow">Scénarios par pôle métier</p>
              <h2
                id="cases-carousel-title"
                className="mt-3 font-display text-3xl font-bold tracking-tight text-balance text-ink md:text-4xl"
              >
                Des projets concrets. <span className="text-gradient">Des chiffres réalistes.</span>
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-stone-600">
                Cinq scénarios représentatifs de nos pôles métier, chiffrés sur les barèmes officiels
                en vigueur. Les vrais dossiers clients sont publiés sur la page Cas clients avec
                l'accord écrit des personnes concernées.
              </p>
            </div>
            {/* Nav arrows desktop */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollByCard(-1)}
                disabled={!canPrev}
                aria-label="Cas client précédent"
                className={cn(
                  'inline-flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-paper text-stone-700 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-150 ease-out',
                  canPrev
                    ? 'hover:-translate-y-0.5 hover:border-stone-300 hover:bg-stone-50 hover:text-ink'
                    : 'cursor-not-allowed opacity-40',
                )}
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => scrollByCard(1)}
                disabled={!canNext}
                aria-label="Cas client suivant"
                className={cn(
                  'inline-flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 bg-paper text-stone-700 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-150 ease-out',
                  canNext
                    ? 'hover:-translate-y-0.5 hover:border-stone-300 hover:bg-stone-50 hover:text-ink'
                    : 'cursor-not-allowed opacity-40',
                )}
              >
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          </div>
        </Reveal>
      </Container>

      {/* Scroller horizontal — full bleed avec padding latéral container */}
      <div
        ref={scrollerRef}
        className="scrollbar-none flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 pb-4 sm:px-6 lg:px-[max(2rem,calc((100vw-1280px)/2+2rem))]"
        style={{ scrollbarWidth: 'none' }}
      >
        <ul className="contents">
          {CASES.map((c, idx) => {
            const Icon = c.sectorIcon;
            const t = TONE[c.tone];
            return (
              <motion.li
                key={c.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: idx * 0.05, ease: easeStripe }}
                className="w-[88vw] shrink-0 snap-start sm:w-[480px] lg:w-[520px]"
              >
                <a
                  href={ROUTES.casClients}
                  className={cn(
                    'group/case relative flex h-full flex-col overflow-hidden rounded-2xl border bg-paper p-6 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-floating md:p-7',
                    'border-stone-200/70',
                  )}
                >
                  {/* Top accent */}
                  <div
                    aria-hidden
                    className={cn(
                      'absolute inset-x-7 top-0 h-px scale-x-0 origin-left bg-gradient-to-r from-brand-500 via-accent-500 to-lavender-500 transition-transform duration-300 ease-out group-hover/case:scale-x-100',
                    )}
                  />
                  {/* Halo coin */}
                  <div
                    aria-hidden
                    className={cn(
                      'pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-300 ease-out group-hover/case:opacity-100',
                      c.tone === 'accent'
                        ? 'bg-accent-300/30'
                        : c.tone === 'brand'
                          ? 'bg-brand-300/30'
                          : c.tone === 'lavender'
                            ? 'bg-lavender-300/30'
                            : 'bg-sage-300/30',
                    )}
                  />

                  {/* Header card */}
                  <div className="flex items-start justify-between gap-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset',
                        t.badge,
                      )}
                    >
                      <Icon className="h-3 w-3" strokeWidth={2} />
                      {c.sector}
                    </span>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-700',
                      )}
                    >
                      {c.pole}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="mt-4 font-display text-[17px] font-semibold leading-tight text-ink md:text-lg">
                    {c.title}
                  </h3>
                  <p className="mt-1.5 text-[12px] text-stone-500">
                    {c.client} · {c.region}
                  </p>

                  {/* Métriques 3 colonnes */}
                  <div className="mt-5 grid grid-cols-3 gap-2 rounded-xl bg-stone-50/70 p-3">
                    {c.metrics.map((m) => (
                      <div key={m.label} className="text-center">
                        <p className="font-display text-base font-bold tracking-tight text-ink tabular-nums md:text-[17px]">
                          {m.value}
                        </p>
                        <p className="mt-0.5 text-[10px] leading-tight text-stone-500">{m.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Disclaimer scénario */}
                  <p className="mt-4 text-[12px] italic leading-relaxed text-stone-500">
                    Scénario type — chiffres indicatifs basés sur les barèmes en vigueur, ne
                    constitue pas un engagement contractuel.
                  </p>

                  {/* Footer */}
                  <div className="mt-auto flex items-center justify-between border-t border-stone-200/60 pt-4">
                    <span className="inline-flex items-center gap-1 font-mono text-[11px] font-bold text-ink">
                      <TrendingUp className="h-3 w-3 text-brand-600" strokeWidth={2.4} />
                      Scénario documenté
                    </span>
                    <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700">
                      Voir les détails
                      <ArrowUpRight
                        className="h-3 w-3 transition-transform duration-150 ease-out group-hover/case:translate-x-0.5 group-hover/case:-translate-y-0.5"
                        strokeWidth={2.2}
                      />
                    </span>
                  </div>
                </a>
              </motion.li>
            );
          })}
        </ul>
      </div>

      {/* Dot indicators (mobile + desktop) */}
      <Container className="mt-6 flex items-center justify-center gap-1.5">
        {CASES.map((c, idx) => (
          <button
            key={c.slug}
            type="button"
            aria-label={`Aller au cas ${idx + 1}`}
            onClick={() => scrollToIdx(idx)}
            className={cn(
              'h-1.5 rounded-full transition-all duration-200 ease-out',
              activeIdx === idx
                ? 'w-6 bg-ink'
                : 'w-1.5 bg-stone-300 hover:bg-stone-400',
            )}
          />
        ))}
      </Container>
    </section>
  );
}
